// Lib
import React from 'react';
import { throttle } from 'lodash';
import Handsontable from 'handsontable/base';

// Utils
import { MNElement } from '../../../../common/elements/elementModelTypes';
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { TableColWidthsGU, TableContentData } from '../../../../common/table/TableTypes';
import { resizeTable } from '../utils/tableSizeUtils';

interface Props {
    element: MNElement;
    tableContentData: TableContentData;
    tableContentColWidthsGU: TableColWidthsGU;
    gridSize: number;
    isResizing: boolean;
    tempSize: { width: number; height: number } | null;
    hotTableInstanceRef: React.MutableRefObject<Handsontable.Core | null>;
    getContextZoomScale: () => number;
}

class TableResizeHandlers extends React.Component<Props> {
    adjustTableSize = (): void => {
        const {
            element,
            tableContentData,
            tableContentColWidthsGU,
            gridSize,
            isResizing,
            tempSize,
            hotTableInstanceRef,
            getContextZoomScale,
        } = this.props;

        // Adjust columns and rows with drag handle
        if (!isResizing || !tempSize || !hotTableInstanceRef.current) return;

        const source = 'resizeHandle';
        const nRows = hotTableInstanceRef.current.countRows();
        const nCols = hotTableInstanceRef.current.countCols();

        const { rowDif, columnDif } =
            resizeTable(
                getElementId(element),
                tableContentData,
                tableContentColWidthsGU,
                tempSize,
                gridSize,
                hotTableInstanceRef.current,
                getContextZoomScale,
            ) ?? {};

        if (rowDif > 0) {
            hotTableInstanceRef.current.alter('insert_row_below', nRows, rowDif, source);
        } else if (rowDif < 0) {
            const numberToRemove = -rowDif;
            hotTableInstanceRef.current.alter('remove_row', nRows - numberToRemove, numberToRemove, source);
        }

        if (columnDif > 0) {
            hotTableInstanceRef.current.alter('insert_col_end', nCols, columnDif, source);
        } else if (columnDif < 0) {
            const numberToRemove = -columnDif;
            hotTableInstanceRef.current.alter('remove_col', nCols - numberToRemove, numberToRemove, source);
        }
    };

    throttleAdjustTableSize = throttle(this.adjustTableSize, 50);

    componentDidUpdate(prevProps: Props): void {
        if (this.props.tempSize !== prevProps.tempSize) {
            this.throttleAdjustTableSize();
        }
    }

    render() {
        return null;
    }
}

export default TableResizeHandlers;
